import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronRight, ExternalLink } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  Separator,
  Tooltip,
  cn,
  useSharedTooltipProvider,
} from '@wit-support/ui';
import { useBreadcrumbDetailLabel, type AdminClientModule } from '@wit-support/ui-domain';
import {
  activeSectionHref,
  breadcrumbsFor,
  headerTabSections,
  sectionHref,
  sidebarTree,
  type SidebarEntry,
  type SidebarGroup,
  type SidebarNode,
} from '../lib/workspace-nav.js';
import {
  isSidebarGroupExpanded,
  setSidebarGroupExpanded,
  useSidebarGroups,
} from '../lib/sidebar-groups.js';
import type { ExternalNavLink } from '../lib/external-links.js';

/**
 * Навигационные части шелла: боковое меню, хлебные крошки, вкладки
 * подразделов. Отдельно от `app-shell.tsx`, чтобы их можно было проверить
 * тестами без реестра модулей и PWA-регистрации, которые тянет `app.tsx`.
 */

type SidebarItemState = SidebarEntry['state'] | 'ancestor';

/** Классы пункта бокового меню — общие для разделов, групп и внешних ссылок. */
function sidebarItemClass(
  collapsed: boolean,
  depth: SidebarEntry['depth'],
  state: SidebarItemState,
): string {
  return [
    'group flex items-center rounded-[var(--radius-2)] border-l-[3px] text-sm transition-colors',
    collapsed
      ? 'justify-center px-0 py-2'
      : depth === 1
        ? 'gap-2 py-1.5 pl-6 pr-2'
        : 'gap-2 px-2 py-1.5',
    state === 'current'
      ? 'border-[var(--color-brand-bg)] bg-[var(--color-surface-raised)] font-medium'
      : state === 'ancestor'
        ? 'border-transparent font-medium hover:bg-[var(--color-surface-raised)]'
        : 'border-transparent hover:bg-[var(--color-surface-raised)]',
  ].join(' ');
}

const ICON_CLASS =
  'h-4 w-4 shrink-0 text-[var(--color-text-muted)] group-[.active]:text-[var(--color-text-primary)]';

/** Ссылка-пункт меню (раздел или подраздел). */
function SidebarLink({
  entry,
  collapsed,
  onNavigate,
  tooltip,
}: {
  entry: SidebarEntry;
  collapsed: boolean;
  onNavigate: () => void;
  /** Подсказка в узком режиме вместо `title` (подраздел группы: «Группа · Подраздел»). */
  tooltip?: string;
}): React.ReactElement {
  const Icon = entry.icon;
  const link = (
    <Link
      to={entry.href}
      onClick={onNavigate}
      aria-label={entry.label}
      aria-current={entry.state === 'current' ? 'page' : undefined}
      title={collapsed && !tooltip ? entry.label : undefined}
      className={sidebarItemClass(collapsed, collapsed ? 0 : entry.depth, entry.state)}
    >
      <Icon className={ICON_CLASS} aria-hidden="true" />
      {collapsed ? null : <span className="truncate">{entry.label}</span>}
    </Link>
  );
  if (!collapsed || !tooltip) return link;
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>{link}</Tooltip.Trigger>
      <Tooltip.Content side="right">{tooltip}</Tooltip.Content>
    </Tooltip.Root>
  );
}

/** Подсказка у заголовка группы, которую сейчас свернуть нельзя. */
export const GROUP_LOCKED_HINT = 'Нельзя свернуть раздел, в котором вы сейчас';

/**
 * Сворачиваемый раздел меню (disclosure). Заголовок — кнопка кита, а не
 * ссылка: смешивать «перейти» и «свернуть» в одном элементе нельзя, а корень
 * модуля и так доступен своим подразделом. Подразделы остаются в DOM и
 * прячутся атрибутом `hidden`, чтобы `aria-controls` всегда указывал на
 * существующий список.
 *
 * Группу с открытым экраном свернуть нельзя (условие владельца: текущий раздел
 * не прячется ни от глаз, ни от скринридера). Кнопка остаётся в порядке Tab —
 * `aria-disabled`, а не `disabled`, — клик ничего не делает, подсказка
 * объясняет почему.
 */
function SidebarGroupSection({
  group,
  expanded,
  onToggle,
  onNavigate,
}: {
  group: SidebarGroup;
  /** Уже с учётом открытого экрана: группа с ним всегда раскрыта. */
  expanded: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}): React.ReactElement {
  const listId = React.useId();
  const Icon = group.icon;
  const locked = group.containsCurrent;
  // Подсказка управляется сама и открывается только у запертой группы. Обёртка
  // есть всегда: иначе при переходе в группу кнопка перемонтировалась бы и
  // теряла фокус.
  const [hintOpen, setHintOpen] = React.useState(false);
  // Подсвечен подраздел; заголовок группы с открытым экраном выделен шрифтом.
  const state: SidebarItemState = locked ? 'ancestor' : 'none';
  const button = (
    <Button
      type="button"
      variant="ghost"
      aria-expanded={expanded}
      aria-controls={listId}
      aria-disabled={locked ? true : undefined}
      onClick={locked ? undefined : onToggle}
      className={cn(
        sidebarItemClass(false, 0, state),
        'h-auto w-full justify-start text-left text-[var(--color-text-primary)]',
        state === 'none' ? 'font-normal' : 'font-medium',
        locked ? 'cursor-default' : null,
      )}
    >
      <Icon className={ICON_CLASS} aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate">{group.label}</span>
      <ChevronRight
        className={cn(
          'h-4 w-4 shrink-0 text-[var(--color-text-muted)] transition-transform',
          expanded ? 'rotate-90' : null,
        )}
        aria-hidden="true"
      />
    </Button>
  );
  return (
    <div className="flex flex-col gap-1">
      <Tooltip.Root open={locked && hintOpen} onOpenChange={setHintOpen}>
        <Tooltip.Trigger asChild>{button}</Tooltip.Trigger>
        {locked ? <Tooltip.Content side="right">{GROUP_LOCKED_HINT}</Tooltip.Content> : null}
      </Tooltip.Root>
      <ul id={listId} hidden={!expanded} aria-label={group.label} className="flex flex-col gap-1">
        {group.children.map((child) => (
          <li key={child.key} className="flex flex-col">
            <SidebarLink entry={child} collapsed={false} onNavigate={onNavigate} />
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Группа в узком (только иконки) сайдбаре. Раскрытая — иконки подразделов
 * между тонкими разделителями, подсказка называет группу и подраздел;
 * свёрнутая — одна иконка модуля, клик открывает меню подразделов справа.
 * В обоих случаях до любого подраздела один-два клика.
 */
function CollapsedSidebarGroup({
  group,
  expanded,
  onNavigate,
  separatorBefore,
  separatorAfter,
}: {
  group: SidebarGroup;
  expanded: boolean;
  onNavigate: () => void;
  /** Разделители раскрытой группы; между двумя соседними группами — один. */
  separatorBefore: boolean;
  separatorAfter: boolean;
}): React.ReactElement {
  if (expanded) {
    return (
      <div role="group" aria-label={group.label} className="flex flex-col gap-1">
        {separatorBefore ? <Separator className="my-1" /> : null}
        {group.children.map((child) => (
          <SidebarLink
            key={child.key}
            entry={child}
            collapsed
            onNavigate={onNavigate}
            tooltip={`${group.label} · ${child.label}`}
          />
        ))}
        {separatorAfter ? <Separator className="my-1" /> : null}
      </div>
    );
  }
  const Icon = group.icon;
  return (
    <DropdownMenu.Root>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <DropdownMenu.Trigger asChild>
            <Button
              type="button"
              variant="ghost"
              aria-label={`${group.label}: подразделы`}
              className={cn(
                sidebarItemClass(true, 0, group.containsCurrent ? 'current' : 'none'),
                'h-auto w-full',
              )}
            >
              <Icon className={ICON_CLASS} aria-hidden="true" />
            </Button>
          </DropdownMenu.Trigger>
        </Tooltip.Trigger>
        <Tooltip.Content side="right">{group.label}</Tooltip.Content>
      </Tooltip.Root>
      <DropdownMenu.Content side="right" align="start">
        <DropdownMenu.Label>{group.label}</DropdownMenu.Label>
        {group.children.map((child) => {
          const ChildIcon = child.icon;
          const current = child.state === 'current';
          return (
            <DropdownMenu.Item key={child.key} asChild>
              <Link
                to={child.href}
                onClick={onNavigate}
                aria-current={current ? 'page' : undefined}
                className={current ? 'font-medium' : undefined}
              >
                <ChildIcon
                  className="h-4 w-4 shrink-0 text-[var(--color-text-muted)]"
                  aria-hidden="true"
                />
                {child.label}
              </Link>
            </DropdownMenu.Item>
          );
        })}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

/** Подсказки работают и вне общего провайдера оболочки (тест, сторибук). */
function WithTooltipProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const shared = useSharedTooltipProvider();
  return shared ? <>{children}</> : <Tooltip.Provider>{children}</Tooltip.Provider>;
}

export function SidebarNav({
  modules,
  sectionLabel,
  currentPath,
  onNavigate,
  collapsed,
  moduleHref,
}: {
  modules: readonly AdminClientModule[];
  /**
   * Имя активного пространства над списком; `null` — заголовка нет. Обычно
   * именно `null`: название несёт переключатель, и дублировать его здесь
   * нельзя. Заполняется только там, где переключателя нет.
   */
  sectionLabel: string | null;
  currentPath: string;
  onNavigate: () => void;
  collapsed: boolean;
  /** Переопределение адреса пункта модуля (`null` — база модуля). */
  moduleHref?: (m: AdminClientModule) => string | null;
}): React.ReactElement {
  const nodes = sidebarTree(modules, currentPath, moduleHref);
  const groups = useSidebarGroups();
  // Группа с открытым экраном раскрыта всегда, что бы ни лежало в хранилище:
  // текущий раздел не прячется (свернуть её кнопкой тоже нельзя).
  const isGroupExpanded = (g: SidebarGroup) => g.containsCurrent || groups.isExpanded(g.key);
  const isExpandedGroup = (n: SidebarNode) => n.kind === 'group' && isGroupExpanded(n);

  // При переходе в свёрнутую группу её раскрытие ещё и запоминается: уйдя с
  // экрана, оператор найдёт группу такой же, какой видел. Читаем хранилище
  // напрямую, а не из снимка — эффекту нужен только путь.
  const currentGroupKey =
    nodes.find((n): n is SidebarGroup => n.kind === 'group' && n.containsCurrent)?.key ?? null;
  React.useEffect(() => {
    if (currentGroupKey !== null && !isSidebarGroupExpanded(currentGroupKey)) {
      setSidebarGroupExpanded(currentGroupKey, true);
    }
  }, [currentPath, currentGroupKey]);

  return (
    <WithTooltipProvider>
      <nav aria-label="Разделы" className="flex flex-col gap-1">
        {collapsed || !sectionLabel ? null : (
          <div className="px-2 pb-1 text-[11px] font-medium uppercase tracking-wider text-[var(--color-text-muted)]">
            {sectionLabel}
          </div>
        )}
        {nodes.map((node, i) => {
          if (node.kind === 'item') {
            return (
              <SidebarLink
                key={node.key}
                entry={node}
                collapsed={collapsed}
                onNavigate={onNavigate}
              />
            );
          }
          const expanded = isGroupExpanded(node);
          return collapsed ? (
            <CollapsedSidebarGroup
              key={node.key}
              group={node}
              expanded={expanded}
              onNavigate={onNavigate}
              separatorBefore={i > 0}
              separatorAfter={i < nodes.length - 1 && !isExpandedGroup(nodes[i + 1]!)}
            />
          ) : (
            <SidebarGroupSection
              key={node.key}
              group={node}
              expanded={expanded}
              onToggle={() => groups.setExpanded(node.key, !expanded)}
              onNavigate={onNavigate}
            />
          );
        })}
      </nav>
    </WithTooltipProvider>
  );
}

/**
 * Внешние ссылки под списком разделов («Документация MCP»): тот же вид, что у
 * пункта меню, но обычный `<a>` в новую вкладку — это не маршрут SPA.
 * `noopener` (+ `noreferrer`): открытая страница не получает `window.opener`.
 * `null`, если ссылок нет.
 */
export function ExternalNavLinks({
  links,
  collapsed,
  onNavigate,
}: {
  links: readonly ExternalNavLink[];
  collapsed: boolean;
  onNavigate: () => void;
}): React.ReactElement | null {
  if (links.length === 0) return null;
  return (
    <nav aria-label="Ссылки" className="flex flex-col gap-1">
      {links.map((l) => {
        const Icon = l.icon;
        return (
          <a
            key={l.key}
            href={l.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onNavigate}
            aria-label={`${l.label} (откроется в новой вкладке)`}
            title={collapsed ? l.label : undefined}
            className={sidebarItemClass(collapsed, 0, 'none')}
          >
            <Icon className="h-4 w-4 shrink-0 text-[var(--color-text-muted)]" aria-hidden="true" />
            {collapsed ? null : (
              <>
                <span className="min-w-0 flex-1 truncate">{l.label}</span>
                <ExternalLink
                  className="h-3.5 w-3.5 shrink-0 text-[var(--color-text-muted)]"
                  aria-hidden="true"
                />
              </>
            )}
          </a>
        );
      })}
    </nav>
  );
}

/**
 * Хлебные крошки шапки. Деталь (имя клипа и т.п.) приходит со страницы через
 * `useBreadcrumbDetail` — компонент обязан стоять под `BreadcrumbDetailProvider`.
 */
export function ShellBreadcrumbs({
  modules,
  navModules,
  path,
  className,
  collapseLeading = false,
}: {
  modules: readonly AdminClientModule[];
  navModules: readonly AdminClientModule[];
  path: string;
  className: string;
  /**
   * Узкий навбар (рядом тулбар страницы): ниже `xl` прячется первая крошка
   * (пространство), ниже `lg` — все, кроме последней (`display:none`, без
   * невидимых остановок Tab). Без пропа разметка прежняя.
   */
  collapseLeading?: boolean;
}): React.ReactElement {
  const detail = useBreadcrumbDetailLabel();
  const crumbs = breadcrumbsFor({ modules, navModules, path, detail });
  const last = crumbs.length - 1;
  // Класс крошки при collapseLeading; разделитель перед крошкой прячется вместе с
  // предыдущей. `undefined` — как без пропа.
  const collapse = (i: number): string | undefined =>
    !collapseLeading || i >= last ? undefined : i === 0 ? 'max-xl:hidden' : 'max-lg:hidden';
  return (
    <nav aria-label="Хлебные крошки" className={className}>
      {crumbs.map((c, i) => (
        <React.Fragment key={`${i}:${c.label}`}>
          {i > 0 ? (
            <span
              className={['px-2', collapse(i - 1)].filter(Boolean).join(' ')}
              aria-hidden="true"
            >
              /
            </span>
          ) : null}
          {i === last ? (
            <span aria-current="page" className="text-[var(--color-text-primary)]">
              {c.label}
            </span>
          ) : c.href ? (
            <Link
              to={c.href}
              className={['hover:text-[var(--color-text-primary)]', collapse(i)]
                .filter(Boolean)
                .join(' ')}
            >
              {c.label}
            </Link>
          ) : (
            <span className={collapse(i)}>{c.label}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

/**
 * Вкладки подразделов в навбаре — только у модуля в режиме `'tabs'` (по
 * умолчанию). `null`, если вкладок нет: модуль без подразделов или в режиме
 * `'sidebar'`.
 */
export function HeaderSectionTabs({
  module,
  path,
}: {
  module: AdminClientModule | undefined;
  path: string;
}): React.ReactElement | null {
  const sections = headerTabSections(module);
  if (!module || sections.length === 0) return null;
  const activeSection = activeSectionHref(module, path);
  return (
    <>
      <nav
        aria-label={`Подразделы: ${module.label}`}
        className="flex min-w-0 items-stretch gap-1 self-stretch overflow-x-auto"
      >
        {sections.map((s) => {
          const href = sectionHref(module, s);
          const active = href === activeSection;
          return (
            <Link
              key={s.path}
              to={href}
              aria-current={active ? 'page' : undefined}
              className={[
                'flex items-center whitespace-nowrap border-b-2 px-3 text-sm transition-colors',
                active
                  ? 'border-[var(--color-brand-bg)] font-medium text-[var(--color-text-primary)]'
                  : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]',
              ].join(' ')}
            >
              {s.label}
            </Link>
          );
        })}
      </nav>
      {/* Свободное место ПОСЛЕ вкладок — иначе меню пользователя на
          узком экране прижалось бы к ним вместо правого края. */}
      <div className="flex-1" />
    </>
  );
}
